import { getEstimatedViewportType } from "@faire/browser-env/getEstimatedViewportType";
import { useIsSSR } from "@faire/ssr/isSSR";
import { useCallback, useEffect, useMemo, useState } from "react";

import { debounceIfPossible } from "./debounceIfPossible";
import { getWindowHeight, getWindowWidth } from "./getWindowDimensions";
import { useHandleResize } from "./useHandleResize";

const getWindowDimension = (): { width: number; height: number } => {
  const width = getWindowWidth();
  const height = getWindowHeight();

  // When the window is momentarily unavailable, getWindowWidth/Height return 0.
  // Treating a 0 width as a real measurement makes getViewportValues report
  // isMobile (0 < tablet breakpoint), which can permanently lock first-render
  // state (e.g. a tooltip's `useState(!isMobile)`). The previous store-backed
  // implementation seeded width from getEstimatedViewportType() and never
  // surfaced 0, so fall back to the estimate to preserve that invariant.
  if (width <= 0) {
    const estimate = getEstimatedViewportType();
    return { width: estimate.width, height: estimate.height };
  }

  return { width, height };
};

export const useViewportDimension = <T extends number | boolean | string>(
  deriveFromDimension: (dimension: { width: number; height: number }) => T
): T => {
  const isSSR = useIsSSR();

  const initialValue = useMemo(() => {
    if (isSSR) {
      const { width, height } = getEstimatedViewportType();
      return deriveFromDimension({ width, height });
    }

    return deriveFromDimension(getWindowDimension());
  }, [deriveFromDimension, isSSR]);

  const [value, setValue] = useState(initialValue);

  const handleResize = useCallback(() => {
    setValue(deriveFromDimension(getWindowDimension()));
  }, [deriveFromDimension]);

  const debouncedHandleResize = useMemo(
    () => debounceIfPossible(handleResize, 200),
    [handleResize]
  );

  useHandleResize(debouncedHandleResize);

  // Reconcile the initial (SSR-estimated) value with the real client
  // dimensions once mounted on the client. This runs synchronously inside the
  // effect's commit rather than through the debounced resize handler: a
  // debounced update fires on a timer outside React's commit phase, which
  // delays the correction and, in tests, escapes act() and surfaces spurious
  // "update was not wrapped in act(...)" errors.
  useEffect(() => {
    if (!isSSR) {
      // oxlint-disable-next-line react/react-compiler -- FIXME(CLI-259): auto-suppressed during shared-config migration.
      handleResize();
    }
  }, [isSSR, handleResize]);

  return value;
};
